import React from "react";
import { Switch, Route, Redirect, useLocation, matchPath } from "react-router-dom";
import { PrivateRoute } from "./components/PrivateRoute";

import {
    SignInScreen,
    ForgotPassword,
        ResetPassword,
    Bars,
    BarDetail,
    BarSpecials,
    Users,
    BarManagers,
    Promotions,
    PromotionDetail,
    Regions,
    RegionDetail,
    Placements,
    Analytics,
    LocalAds,
    FeedAds,
    VerifyPromotionPayment,
    VerifyFeaturePlacementPayment,
    TrialActivatedConfirmation,
    PromotionActivatedConfirmation,
    HowTo,
    HowToEditor,
    MigrationAdmin,
    OSMDataAdmin,
    CategoryDefinitions,
    ClaimBar,
    ClaimedBarReview,
    ContactedBars,
    NBLBAdmin,
    NBLBMasterList,
    Blacklist,
    StatewideSearch,
    EmailSystem,
    NotificationSystem,
    SystemConfig
} from "./views";

import { withLayout } from "./components/Layout";
import { Header } from "./header/Header";
import { Navigation } from "./navigation/Navigation";
import { useUserRoles } from "./globalstore";
import { auth } from "./services/firebase";
import { LoadingBlanket } from "./components/LoadingBlanket";
import { FeedAdsProvider } from "./actions/feedAds";

const withBasicLayout = withLayout(Header)(Navigation);

const BarListScreen = withBasicLayout(Bars, { title: "Manage Bars" });
const ManageBarScreen = withBasicLayout(BarDetail, {
    title: "Bar Details"
});
const ManageUsersScreen = withBasicLayout(Users, {
    title: "Manage users"
});
const ManageBarRolesScreen = withBasicLayout(BarManagers, {
    title: "Bar Managers"
});
const BarSpecialsScreen = withBasicLayout(BarSpecials, {
    title: "Bar Specials"
});
const PromotionListScreen = withBasicLayout(Promotions, {
    title: "Promotions"
});
const PromotionListScreenByBar = withBasicLayout(Promotions, {
    title: "Promotions by Bar"
});
const ManagePromotionScreen = withBasicLayout(PromotionDetail, {
    title: "Promotion Details"
});

const RegionListScreen = withBasicLayout(Regions, {
    title: "Regions"
});
const RegionDetailScreen = withBasicLayout(RegionDetail, {
    title: "Manage Regions"
});

const PlacementsScreen = withBasicLayout(Placements, {
    title: "Featured Placements"
});

const AnalyticsScreen = withBasicLayout(Analytics, {
    title: "Analytics"
});

const VerifyPromotionPaymentScreen = withBasicLayout(VerifyPromotionPayment, {
    title: "Verify Promotion Payment"
});

const VerifyFeaturePlacementPaymentScreen = withBasicLayout(VerifyFeaturePlacementPayment, {
    title: "Verify Feature Placement Payment"
});

const TrialActivatedScreen = withBasicLayout(TrialActivatedConfirmation, {
    title: "Trial Activated"
});

const PromotionActivatedScreen = withBasicLayout(PromotionActivatedConfirmation, {
    title: "Promotion Activated"
});

const LocalAdsScreen = withBasicLayout(LocalAds, {
    title: "Local Advertisements"
});

const FeedAdsScreen = withBasicLayout(FeedAds, {
    title: "Feed Advertisements"
});

// New How To screens
const HowToScreen = withBasicLayout(HowTo, {
    title: "How To Guide"
});

const HowToEditorScreen = withBasicLayout(HowToEditor, {
    title: "How To Editor"
});

// New Admin screens
const MigrationAdminScreen = withBasicLayout(MigrationAdmin, {
    title: "Supabase Migration Admin"
});

const OSMDataAdminScreen = withBasicLayout(OSMDataAdmin, {
    title: "OSM Data Collection Admin"
});

const NBLBScreen = withBasicLayout(NBLBAdmin, {
    title: "No Bar Left Behind"
});

const NBLBMasterListScreen = withBasicLayout(NBLBMasterList, {
    title: "No Bar Left Behind Master List"
});

const StatewideSearchScreen = withBasicLayout(StatewideSearch, {
    title: "Statewide Search"
});

const BlacklistScreen = withBasicLayout(Blacklist, {
    title: "Excluded Venues"
});

const CategoryDefinitionsScreen = withBasicLayout(CategoryDefinitions, {
    title: "Category Definitions"
});

const ClaimBarScreen = withBasicLayout(ClaimBar, {
    title: "Claim Your Bar"
});

const ClaimedBarReviewScreen = withBasicLayout(ClaimedBarReview, {
    title: "Claimed Bar Review"
});

const ContactedBarsScreen = withBasicLayout(ContactedBars, {
    title: "Contacted Bars"
});

const EmailSystemScreen = withBasicLayout(EmailSystem, {
    title: "Email System"
});

const NotificationSystemScreen = withBasicLayout(NotificationSystem, {
    title: "Notification System"
});

const SystemConfigScreen = withBasicLayout(SystemConfig, {
    title: "System Config"
});

function App() {
    const location = useLocation();
    const PUBLIC_PATHS = ["/login", "/forgotpassword", "/claim-bar", "/__/auth/action", "/reset-password"];
    const isPublicRoute = PUBLIC_PATHS.some((path) =>
        matchPath(location.pathname, { path, exact: true })
    );
    const { userRoles: { isAdmin, roles, status } } = useUserRoles();

    if (isPublicRoute) {
        return (
            <Switch>
                <Route exact path="/login" component={SignInScreen} />
                <Route exact path="/forgotpassword" component={ForgotPassword} />
                <Route exact path="/__/auth/action" component={ResetPassword} />
                <Route exact path="/reset-password" component={ResetPassword} />
                <Route
                    exact
                    path="/claim-bar"
                    render={(props) =>
                        auth.currentUser ? (
                            <ClaimBarScreen {...props} />
                        ) : (
                            <ClaimBar {...props} />
                        )
                    }
                />
                <Redirect to="/login" />
            </Switch>
        );
    }
    if (status === "fetching") {
        return <LoadingBlanket />;
    }
    return (
        <FeedAdsProvider>
            <div className="App">
                <Switch>
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/users"
                        component={ManageUsersScreen}
                    />
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/bars"
                        component={BarListScreen}
                    />
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/bar/new"
                        component={ManageBarScreen}
                    />
                    <PrivateRoute
                        exact
                        path="/promotions"
                        component={PromotionListScreen}
                    />
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/regions"
                        component={RegionListScreen}
                    />
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/region/:id"
                        component={RegionDetailScreen}
                    />
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/local-ads"
                        component={LocalAdsScreen}
                    />
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/feed-ads"
                        component={FeedAdsScreen}
                    />
                    {/* How To routes */}
                    <PrivateRoute
                        exact
                        path="/how-to"
                        component={HowToScreen}
                    />
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/how-to-editor"
                        component={HowToEditorScreen}
                    />
                    {/* Admin Development Tools */}
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/admin/migration"
                        component={MigrationAdminScreen}
                    />
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/admin/osm-data"
                        component={OSMDataAdminScreen}
                    />
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/admin/category-definitions"
                        component={CategoryDefinitionsScreen}
                    />
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/admin/contacted-bars"
                        component={ContactedBarsScreen}
                    />
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/admin/nblb"
                        component={NBLBScreen}
                    />
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/admin/statewide-search"
                        component={StatewideSearchScreen}
                    />
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/admin/nblb-master"
                        component={NBLBMasterListScreen}
                    />
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/admin/blacklist"
                        component={BlacklistScreen}
                    />
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/admin/email-system"
                        component={EmailSystemScreen}
                    />
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/admin/notification-system"
                        component={NotificationSystemScreen}
                    />
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/admin/system-config"
                        component={SystemConfigScreen}
                    />
                    <PrivateRoute
                        adminOnly
                        exact
                        path="/admin/claimed-bar-review"
                        component={ClaimedBarReviewScreen}
                    />
                    <PrivateRoute
                        path="/bar/:id"
                        component={ManageBarScreen}
                    />
                    <PrivateRoute
                        path="/managers/:id"
                        component={ManageBarRolesScreen}
                    />
                    <PrivateRoute
                    path="/specials/:id"
                    component={BarSpecialsScreen}
                />
                <PrivateRoute
                        path="/promotions/:id"
                        component={PromotionListScreenByBar}
                    />
                    <PrivateRoute
                        path="/promotion/:id"
                        component={ManagePromotionScreen}
                    />
                    <PrivateRoute
                        path="/premium/placements/:id"
                        component={PlacementsScreen}
                    />
                    <PrivateRoute
                        path="/premium/analytics"
                        component={AnalyticsScreen}
                    />
                    <PrivateRoute
                        path="/verifyPromotionPayment"
                        component={VerifyPromotionPaymentScreen}
                    />
                    <PrivateRoute
                        path="/verifyFeaturePlacementPayment"
                        component={VerifyFeaturePlacementPaymentScreen}
                    />
                <PrivateRoute
                    path="/trial-activated/:barId/:promotionId" // Use params if needed on the page
                    component={TrialActivatedScreen}
                />
                <PrivateRoute
                    path="/promotion-activated/:barId/:promotionId"
                    component={PromotionActivatedScreen}
                />
                    <Route
                        path="/"
                        render={(props) => {
                            if (status === "fetching") return <LoadingBlanket />;
                            if (!auth.currentUser) return <Redirect to="/login" {...props} />;
                            if (isAdmin) return <Redirect to="/bars" {...props} />;
                            if (roles && Object.keys(roles).length > 0) {
                                return (
                                    <Redirect
                                        to={`/bar/${Object.keys(roles)[0]}`}
                                        {...props}
                                    />
                                );
                            }
                            return <Redirect to="/claim-bar" {...props} />;
                        }}
                    />
                </Switch>
            </div>
        </FeedAdsProvider>

    );
}

export default App;